import { useState, useMemo, useContext, useEffect } from 'react'
import FeatherIcon from 'feather-icons-react'
import { Button } from 'react-bootstrap'
import objectPath from 'object-path'
import colorContrast from 'color-contrast'
import { contextMenu, Item, Menu } from 'react-contexify'
import 'react-contexify/dist/ReactContexify.min.css'
import './custom-contexify.css'
import { DesignContext } from 'contexts/DesignContext'
import FavoriteModal from './favorite/Modal'
import ModalCreate from './favorite/ModalCreate'
import ModalSelect from './favorite/ModalSelect'

const FavoriteButton = ({ uischema, disabled = false, data }) => {
  const designContext = useContext(DesignContext)
  const { jsonForm, setDesignerPropsData, getCurrentUi, elementAction } =
    designContext
  const { formData = {}, designerPropsData = {} } = jsonForm
  const [isActive, setIsActive] = useState(false)
  const [isHover, setIsHover] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [modalCode, setModalCode] = useState({})
  const [menu, setMenu] = useState({})
  const {
    key,
    favoriteColor = '#FFB64D',
    favoriteBgColor,
    favorite,
    buttonText = 'Favorite',
    buttonColor = '#4099ff',
  } = uischema
  const { enabled = false, isButton } = favorite || {}
  const isTable = ['table', 'customTable'].includes(uischema.tagname)

  const [options, setOptions] = useState([])
  const [selectFavorite, setSelectFavorite] = useState({})
  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  let hasEnable = objectPath.has(elementAction, ['isEnable', key])
  let isEnable = objectPath.get(elementAction, ['isEnable', key])

  const isDisable = useMemo(() => {
    if (hasEnable) {
      let isDisable = isEnable === false ? true : false
      return isDisable
    }
    return disabled
  }, [key, isEnable])

  const buttonType = isTable ? 'outline' : uischema.buttonType
  const initialColor = isTable ? favoriteColor : buttonColor
  const [styleChange, setStyleChange] = useState({})

  const handleMouseEnter = () => {
    setIsHover(true)
  }

  const handleMouseLeave = () => {
    setIsHover(false)
  }

  const cursor = useMemo(() => {
    if (isHover) return 'pointer'
    return 'default'
  }, [isHover])

  const backgroundColor = useMemo(() => {
    if (isHover) return favoriteBgColor
    if (isActive) return favoriteColor
    return 'transparent'
  }, [isActive, isHover])

  const color = useMemo(() => {
    if (isActive) return '#fff'
    return favoriteColor
  }, [isActive, favoriteColor])

  const getLightDarkColor = (col, amt) => {
    let usePound = false
    if (col[0] == '#') {
      col = col.slice(1)
      usePound = true
    }
    let num = parseInt(col, 16)
    let r = (num >> 16) + amt
    if (r > 255) r = 255
    else if (r < 0) r = 0
    let b = ((num >> 8) & 0x00ff) + amt
    if (b > 255) b = 255
    else if (b < 0) b = 0
    let g = (num & 0x0000ff) + amt
    if (g > 255) g = 255
    else if (g < 0) g = 0
    return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16)
  }

  const getColorText = (color) => {
    let contrast = colorContrast('#fff', color)
    if (contrast >= 1.5) {
      return '#fff'
    }
    return '#000'
  }

  const getColorHover = (color) => {
    let contrast = colorContrast('#fff', color)
    if (contrast >= 2) {
      return getLightDarkColor(initialColor, 20)
    }
    return getLightDarkColor(initialColor, -20)
  }

  const styles = {
    default: {
      color: getColorText(initialColor),
      borderColor: initialColor,
      backgroundColor: initialColor,
    },
    defaultHover: {
      borderColor: getColorHover(initialColor),
      backgroundColor: getColorHover(initialColor),
    },
    outline: {
      color: initialColor,
      borderColor: initialColor,
      backgroundColor: 'transparent',
    },
    outlineHover: {
      color: getColorText(initialColor),
      borderColor: initialColor,
      backgroundColor: initialColor,
    },
    icon: {
      color: initialColor,
      borderColor: initialColor,
      backgroundColor: 'transparent',
      cursor: 'pointer',
    },
    left: {
      marginLeft: '0',
      marginRight: 'auto',
    },
    center: {
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    right: {
      marginLeft: 'auto',
      marginRight: '0',
    },
  }

  const handleEvent = (e) => {
    contextMenu.show({
      id: key,
      event: e,
    })
  }

  const handleHover = (isEnter) => {
    if (isEnter) {
      if (buttonType === 'outline') {
        setStyleChange({ ...styleChange, ...styles.outlineHover })
      } else {
        setStyleChange({ ...styleChange, ...styles.defaultHover })
      }
    } else {
      if (buttonType === 'outline') {
        setStyleChange({ ...styleChange, ...styles.outline })
      } else {
        setStyleChange({ ...styleChange, ...styles.default })
      }
    }
  }

  const handleClickMenu = async (item, isCreate) => {
    objectPath.set(item, 'modalSize', isCreate ? 'lg' : 'xl')
    setMenu(item)
    let widgetCode = objectPath.get(item, 'design.code') || null
    setModalCode(widgetCode)
    setIsOpen(true)
    setIsActive(false)
    let { props = [] } = item
    let { jsonData = {} } = selectFavorite
    let designerData = {}
    let uiList = []
    props.forEach(({ prop, systemType, systemKey, systemValue }) => {
      if (systemType === 'custom') {
        objectPath.set(designerData, prop, systemValue)
      } else if (systemType === 'input') {
        let value = objectPath.get(formData, systemKey)
        objectPath.set(designerData, prop, value)
      } else if (systemType === 'jsonData') {
        if (selectFavorite?.mapKey) {
          let mapKeyList = selectFavorite.mapKey
          mapKeyList.forEach((key) => {
            let ui = getCurrentUi(key)
            let name = ui.name
            let value = objectPath.get(formData, key)
            objectPath.set(designerData, `${prop}.${name}`, value)
            let foundIndex = uiList.findIndex((item) => item.key === ui.key)
            if (ui.tagname === 'widget') {
              let { widget } = ui
              let designerProps =
                objectPath.get(designerPropsData, 'props') || []
              let widgetProps = objectPath.get(widget, 'receiveProps') || []
              widgetProps.forEach((item) => {
                let found = designerProps.find(
                  ({ propKey }) => propKey === item.key,
                )
                if (found) {
                  let { system = [] } = found
                  system.forEach(({ field, systemKey, systemName }) => {
                    let value =
                      objectPath.get(formData, field) ||
                      objectPath.get(formData, systemName) ||
                      objectPath.get(formData, systemKey)
                    objectPath.set(designerData, `${prop}.${field}`, value)
                    objectPath.set(
                      ui,
                      `jsonDataList.${field}`,
                      `jsonData.${field}`,
                    )
                  })
                }
              })
              uiList.push(ui)
              objectPath.del(designerData, `${prop}.${name}`)
            } else if (ui.tagname === 'component') {
              let receiveProps = objectPath.get(designerPropsData, 'receive')
              let foundProp =
                receiveProps.find(({ propKey }) => propKey === ui.key) || {}
              let { system = [] } = foundProp

              system.forEach(({ systemName }) => {
                let value = objectPath.get(formData, systemName)
                objectPath.set(designerData, `${prop}.${systemName}`, value)
              })
              let addUi = { ...ui }
              objectPath.del(addUi, 'favorite')
              uiList.push(addUi)
              objectPath.del(designerData, `${prop}.${name}`)
            } else if (ui.tagname === 'checkboxInput') {
              let input = {
                ...ui.input,
                name: `jsonData.${ui.input.name}`,
              }
              if (foundIndex > -1) {
                let editUi = uiList[foundIndex]
                objectPath.set(editUi, 'input', input)
              } else {
                let addUi = { ...ui, name: `jsonData.${ui.name}`, input: input }
                objectPath.del(addUi, 'favorite')
                uiList.push(addUi)
              }
            } else if (ui.tagname === 'checkboxGroup') {
              if (foundIndex > -1) {
                let editUi = uiList[foundIndex]
                let options = [...ui.options]
                let optionIndex = options.findIndex(
                  (option) => option?.key === key || option?.input?.key === key,
                )
                let option = objectPath.get(options, optionIndex)
                let optionName = option.name || ''
                let inputName = option?.input?.name || ''
                if (!optionName.startsWith('jsonData.')) {
                  optionName = `jsonData.${option.name}`
                }
                if (!inputName.startsWith('jsonData.')) {
                  inputName = `jsonData.${option?.input?.name}`
                }
                if (option?.input?.key === key) {
                  objectPath.set(
                    options,
                    [optionIndex, 'input', 'name'],
                    inputName,
                  )
                } else {
                  objectPath.set(options, [optionIndex, 'name'], optionName)
                }
                objectPath.set(editUi, 'options', options)
              } else {
                let addUi = {
                  ...ui,
                  name: `jsonData.${ui.name}`,
                }
                let options = [...ui.options]
                let optionIndex = options.findIndex(
                  (option) => option?.key === key || option?.input?.key === key,
                )
                let option = objectPath.get(options, optionIndex)
                let optionName = option.name || ''
                let inputName = option?.input?.name || ''
                if (!optionName.startsWith('jsonData.')) {
                  optionName = `jsonData.${option.name}`
                }
                if (!inputName.startsWith('jsonData.')) {
                  inputName = `jsonData.${option?.input?.name}`
                }
                if (option?.input?.key === key) {
                  objectPath.set(
                    options,
                    [optionIndex, 'input', 'name'],
                    inputName,
                  )
                } else {
                  objectPath.set(options, [optionIndex, 'name'], optionName)
                }
                objectPath.set(addUi, 'options', options)
                objectPath.del(addUi, 'favorite')
                uiList.push(addUi)
              }
            } else {
              let addUi = { ...ui, name: `jsonData.${ui.name}` }
              objectPath.del(addUi, 'favorite')
              uiList.push(addUi)
            }
          })
        } else {
          Object.keys(jsonData).forEach((key) => {
            let name = jsonData[key]
            let ui =
              objectPath.get(jsonForm, `schema.mapWidget.ui.${key}`) ||
              getCurrentUi(key)
            let value = objectPath.get(formData, key)
            objectPath.set(designerData, `${prop}.${name}`, value)
            let foundIndex = uiList.findIndex((item) => item.key === ui.key)
            if (ui.tagname === 'checkboxInput') {
              let input = {
                ...ui.input,
                name,
                name: `jsonData.${ui.input.name}`,
              }
              if (foundIndex > -1) {
                let editUi = uiList[foundIndex]
                objectPath.set(editUi, 'input', input)
              } else {
                let addUi = { ...ui, name: `jsonData.${ui.name}`, input: input }
                objectPath.del(addUi, 'favorite')
                uiList.push(addUi)
              }
            } else if (ui.tagname === 'checkboxGroup') {
              if (foundIndex > -1) {
                let editUi = uiList[foundIndex]
                let options = [...ui.options]
                let optionIndex = options.findIndex(
                  (option) => option?.key === key || option?.input?.key === key,
                )
                let option = objectPath.get(options, optionIndex)
                let optionName = option.name || ''
                let inputName = option?.input?.name || ''
                if (!optionName.startsWith('jsonData.')) {
                  optionName = `jsonData.${option.name}`
                }
                if (!inputName.startsWith('jsonData.')) {
                  inputName = `jsonData.${option?.input?.name}`
                }
                if (option?.input?.key === key) {
                  objectPath.set(
                    options,
                    [optionIndex, 'input', 'name'],
                    inputName,
                  )
                } else {
                  objectPath.set(options, [optionIndex, 'name'], optionName)
                }
                objectPath.set(editUi, 'options', options)
              } else {
                let addUi = {
                  ...ui,
                  name: `jsonData.${ui.name}`,
                }
                let options = [...ui.options]
                let optionIndex = options.findIndex(
                  (option) => option?.key === key || option?.input?.key === key,
                )
                let option = objectPath.get(options, optionIndex)
                let optionName = option.name || ''
                let inputName = option?.input?.name || ''
                if (!optionName.startsWith('jsonData.')) {
                  optionName = `jsonData.${option.name}`
                }
                if (!inputName.startsWith('jsonData.')) {
                  inputName = `jsonData.${option?.input?.name}`
                }
                if (option?.input?.key === key) {
                  objectPath.set(
                    options,
                    [optionIndex, 'input', 'name'],
                    inputName,
                  )
                } else {
                  objectPath.set(options, [optionIndex, 'name'], optionName)
                }
                objectPath.set(addUi, 'options', options)
                objectPath.del(addUi, 'favorite')
                uiList.push(addUi)
              }
            } else {
              let addUi = { ...ui, name: `jsonData.${ui.name}` }
              objectPath.del(addUi, 'favorite')
              uiList.push(addUi)
            }
          })
        }
      }
    })
    objectPath.set(designerPropsData, 'data', designerData)
    objectPath.set(designerPropsData, 'jsonData', jsonData)
    objectPath.set(designerPropsData, 'favoriteProps', props)
    objectPath.set(designerPropsData, 'ui', uiList)
    setDesignerPropsData(designerPropsData)
    setSelectFavorite({})
    setOptions([])
  }

  useEffect(() => {
    let style = {}
    if (buttonType === 'outline') {
      style = styles.outline
      setStyleChange(styles.outline)
    } else {
      style = styles.default
    }
    if (uischema.buttonPosition === 'center') {
      style = { ...style, ...styles.center }
    } else if (uischema.buttonPosition === 'right') {
      style = { ...style, ...styles.right }
    } else {
      style = { ...style, ...styles.left }
    }
    style = { ...style, width: uischema.buttonWidth }
    setStyleChange(style)
  }, [
    uischema.buttonColor,
    buttonType,
    uischema.buttonWidth,
    uischema.buttonPosition,
  ])

  return (
    <div className="d-contents">
      {enabled && (
        <>
          {isButton || isTable ? (
            <>
              {!isLocked && (
                <>
                  {buttonType === 'icon' ? (
                    <FeatherIcon
                      icon="star"
                      size="16"
                      className="mr-1"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        ...styles.icon,
                      }}
                      onClick={(e) => {
                        setOptions(favorite?.options || [])
                        setSelectFavorite(favorite)
                        handleEvent(e)
                      }}
                      onMouseEnter={() => handleHover(true)}
                      onMouseLeave={() => handleHover(false)}
                      disabled={isDisable}
                    />
                  ) : (
                    <Button
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        ...styleChange,
                      }}
                      onClick={(e) => {
                        setOptions(favorite?.options || [])
                        setSelectFavorite(favorite)
                        handleEvent(e)
                      }}
                      onMouseEnter={() => handleHover(true)}
                      onMouseLeave={() => handleHover(false)}
                      disabled={isDisable}
                    >
                      <FeatherIcon icon="star" size="14" className="mr-1" />
                      {buttonText}
                    </Button>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {!isDisable && !isLocked && (
                <span
                  className="ml-1 p-0 d-flex justify-content-center align-items-center"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    border: 'unset',
                    borderRadius: '50%',
                    width: '30px',
                    height: '30px',
                    backgroundColor: backgroundColor,
                    color: color,
                    cursor: cursor,
                    transition: '0.3s',
                  }}
                  onClick={(e) => {
                    setOptions(favorite?.options || [])
                    setSelectFavorite(favorite)
                    handleEvent(e)
                  }}
                >
                  <FeatherIcon icon="star" size="14" />
                </span>
              )}
            </>
          )}
        </>
      )}
      <Menu id={key}>
        {options.map((item, index) => (
          <Item
            key={`menu-${key}-${index}`}
            onClick={() => handleClickMenu(item, index === 0)}
          >
            <FeatherIcon className="mr-1" icon={item.icon} size="14" />
            {item.name}
          </Item>
        ))}
      </Menu>
      <ModalCreate
        uischema={uischema}
        menu={menu}
        isOpen={isOpen && menu.name === 'สร้างสูตร'}
        setIsOpen={setIsOpen}
        data={data}
        isTable={uischema.tagname === 'table'}
      />
      <ModalSelect
        uischema={uischema}
        menu={menu}
        isOpen={isOpen && menu.name === 'เลือกสูตร'}
        setIsOpen={setIsOpen}
        isTable={uischema.tagname === 'table'}
      />
    </div>
  )
}

export default FavoriteButton
